import { useFeature } from '@growthbook/growthbook-react'
import { styled, Button, ButtonGroup, CircularProgress } from '@mui/material'
import { ConferenceContext } from 'contexts/conference'
import { Icon } from 'hello-design-system'
import { SettingsMenu } from 'components/SettingsMenu'
import { ButtonWrapper, ClassButton, BreakoutButton, EndCallButton } from 'components/buttons'
import { SettingIconButton } from 'components/icon-buttons'
import { BreakoutRoomsState } from 'pages/useBreakouts'
import { Timer } from 'components/timer'
import { useContext, useState, useEffect } from 'react'
import { isGocAssistant } from 'utils/is-goc-assistant'

const CustomButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    button: {
        '&:first-of-type': {
            borderRadius: theme.spacing(2, 0, 0, 2)
        }
    },
    span: {
        button: {
            '&:last-of-type': {
                borderRadius: theme.spacing(0, 2, 2, 0)
            }
        }
    }
}))
const HeaderWrapper = styled('div')(({ theme }) => ({
    boxSizing: 'content-box',
    display: 'flex',
    padding: '0 var(--page-side-margin)',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        padding: 'var(--page-side-margin)'
    }
}))
const LeftWrapper = styled('div')<{ borActive?: boolean }>(({ theme, borActive }) => ({
    backgroundColor: theme.palette.background?.default,
    padding: 'var(--page-v-margin) 0',
    flex: 2,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'start',
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('lg')]: {
        flexDirection: borActive ? 'column' : 'row'
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        padding: 0,
        justifyContent: 'center'
    }
}))
const BreakoutButtonsWrapper = styled('div')(({ theme }) => ({
    display: 'flex'
}))
const CenterWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1
}))
const RightWrapper = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background?.default,
    padding: 'var(--page-v-margin) 0',
    flex: 2,
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'end',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        padding: 0,
        justifyContent: 'center'
    }
}))
const OverseeButton = styled(Button)(({ theme }) => ({
    border: `solid 1px ${theme.palette.grey?.[300]}`,
    borderRadius: '60px',
    '&:hover': {
        border: `solid 1px ${theme.palette.grey?.[300]}`
    }
}))

const SyncRoomsButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.grey?.[100],
    color: theme.palette.primary?.main,
    height: '48px',
    borderRadius: '48px',
    '&:hover': {
        backgroundColor: '#F5F5F5'
    },
    '&:disabled': {
        backgroundColor: '#FFFFFF',
        opacity: 0.5
    }
}))

type ClassMode = {
    breakouts?: BreakoutRoomsState
    viewBorModal?: boolean
    showBorModal?: () => void
    showEditBorModal?: () => void
    toggleLeaveDialog?: () => void
    showExitBORPopup?: () => void
}

const TopControls: React.FC<ClassMode> = ({ breakouts, viewBorModal, showBorModal, showEditBorModal, toggleLeaveDialog, showExitBORPopup }) => {
    const conference = useContext(ConferenceContext)
    const isSyncRoomsEnabled = useFeature('goc-bor-sync-rooms').value as boolean
    const [overseeClicked, setOverseeClicked] = useState(false)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        if (!breakouts?.isTransitioning) {
            setOverseeClicked(false)
        }
    }, [breakouts?.isTransitioning])

    const sessionJoined = conference.status === 'session-joined'

    const onClassButtonClick = (): void => {
        if (breakouts?.isActive && showExitBORPopup) {
            showExitBORPopup()
        } else {
            breakouts?.stop()
        }
    }

    const activeStudentsCount = (): number => conference.participants.filter(s => s.role !== 'teacher' && !isGocAssistant(s)).length

    return (
        <HeaderWrapper>
            <LeftWrapper borActive={breakouts?.isActive}>
                <ButtonWrapper>
                    <CustomButtonGroup
                        size='medium'
                        variant='contained'
                        orientation='horizontal'
                        color='primary'
                    >
                        <ClassButton
                            id='class-button'
                            active={!breakouts?.isActive}
                            disabled={!sessionJoined}
                            onClick={!breakouts?.isActive ? () => null : onClassButtonClick}
                        />
                        <BreakoutButton
                            id='breakout-rooms-button'
                            active={!!breakouts?.isActive}
                            disabled={!sessionJoined || activeStudentsCount() < 4}
                            onClick={breakouts?.isActive ? undefined : showBorModal}
                        />
                    </CustomButtonGroup>
                </ButtonWrapper>
                {breakouts?.isActive ? (
                    <BreakoutButtonsWrapper>
                        <ButtonWrapper>
                            <Timer
                                onDurationChange={requestedValue => breakouts?.setDuration(requestedValue)}
                                startTime={breakouts?.startTime}
                                duration={breakouts?.duration}
                                id='bor-timer'
                                max={50}
                                min={2}
                            />
                        </ButtonWrapper>
                        <ButtonWrapper>
                            <SettingIconButton id='bor-settings-button' onClick={showEditBorModal} />
                        </ButtonWrapper>
                    </BreakoutButtonsWrapper>
                ) : (
                    <></>
                )}
            </LeftWrapper>
            {breakouts?.isActive ? (
                <CenterWrapper>
                    <OverseeButton
                        id='bor-oversee-button'
                        color='secondary'
                        disabled={breakouts.teacherRoom === null || breakouts.isTransitioning}
                        onClick={() => {
                            setOverseeClicked(true)
                            void breakouts.linkTeacher(null)
                        }}
                        startIcon={overseeClicked && breakouts.isTransitioning ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >Oversee</OverseeButton>
                </CenterWrapper>
            ) : null}
            <RightWrapper>
                {breakouts?.isActive && isSyncRoomsEnabled ? (
                    <ButtonWrapper>
                        <SyncRoomsButton
                            id='bor-sync-rooms-button'
                            variant='contained'
                            disableElevation
                            disabled={isSyncing}
                            startIcon={<Icon width={24} iconName='SyncRounded' color='primary' />}
                            onClick={() => {
                                setIsSyncing(true)
                                breakouts?.syncRooms()
                                setTimeout(() => setIsSyncing(false), 3000)
                            }}
                        >Sync Rooms</SyncRoomsButton>
                    </ButtonWrapper>
                ) : null}
                <ButtonWrapper>
                    <SettingsMenu setCurrentSidebar={() => { }} />
                </ButtonWrapper>
                <ButtonWrapper>
                    <EndCallButton text='End Class' disabled={!sessionJoined || breakouts?.isActive} onClick={toggleLeaveDialog} />
                </ButtonWrapper>
            </RightWrapper>
        </HeaderWrapper>
    )
}

export { TopControls }
